import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import Logo from "@/components/Logo";

const formatErr = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
};

const AdminLogin = () => {
  const { user, login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === null) return null; // still checking
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper-cool px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <Logo size="lg" />
        </div>
        <div className="bg-white border border-black/8 rounded-md p-10">
          <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-3 flex items-center gap-2">
            <Lock className="h-3.5 w-3.5" /> [ Admin sign-in ]
          </div>
          <h1 className="font-display font-bold text-[1.75rem] tracking-tightest leading-tight mb-8">
            Calmbreeze dashboard.
          </h1>
          <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
            <label className="block">
              <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-2">Email</div>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-b border-black/15 py-3 text-[15px] focus:outline-none focus:border-brand"
                data-testid="admin-email"
              />
            </label>
            <label className="block">
              <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-2">Password</div>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-transparent border-b border-black/15 py-3 text-[15px] focus:outline-none focus:border-brand"
                data-testid="admin-password"
              />
            </label>
            {error && (
              <div className="text-[12px] text-accent font-mono" data-testid="admin-error">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-submit"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white font-semibold text-[14px] rounded-sm hover:bg-accent transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowUpRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
